import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NOTICIAS } from '../../data/mockNoticias';

const ChevronLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

function NoticiaDetail() {
  const [noticia, setNoticia] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();

  useEffect(() => {
    const fetchNoticia = async () => {
      try {
        // Primero buscamos en el mock
        const mockNoticia = NOTICIAS.find(n => n.slug === slug);
        
        if (mockNoticia) {
          setNoticia(mockNoticia);
        } else {
          // Si no está en el mock, buscamos en la API
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news/${slug}`);
          if (!response.ok) throw new Error('Noticia no encontrada');
          const data = await response.json();
          setNoticia(data);
        }
      } catch (error) {
        console.error('Error fetching noticia:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticia();
  }, [slug]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % noticia.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + noticia.images.length) % noticia.images.length);
  };

  if (loading || !noticia) return null;

  // Determinamos si la noticia es del mock buscándola en NOTICIAS
  const isFromMock = NOTICIAS.some(n => n.slug === noticia.slug);
  
  // Si es del mock, usamos la imagen directamente, si no, usamos la URL tal como viene
  const displayImageUrl = isFromMock 
    ? noticia.images[currentImageIndex] 
    : noticia.images[currentImageIndex];

  return (
    <div className="min-h-screen bg-brand-dark py-20">
      <div className="container mx-auto px-4">
        <Link
          to="/noticias"
          className="inline-block mb-8 text-brand-peach hover:text-brand-white transition-colors duration-200"
        >
          ← Volver a noticias
        </Link>

        <article className="max-w-4xl mx-auto">
          <header className="mb-12 text-center">
            <span className="inline-block px-4 py-1 mb-4 text-sm font-medium text-brand-peach bg-brand-red/20 rounded-full">
              {noticia.type}
            </span>
            <h1 className="text-6xl md:text-7xl font-morganite text-brand-white mb-4">
              {noticia.title}
            </h1>
            {noticia.subtitle && (
              <h2 className="text-2xl font-helvetica text-brand-white/80 mb-4">
                {noticia.subtitle}
              </h2>
            )}
            <time className="text-brand-white/70">
              {new Date(noticia.created_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
          </header>

          <div className="space-y-8">
            <div className="relative aspect-video">
              <img
                src={displayImageUrl}
                alt={`${noticia.title} - Imagen ${currentImageIndex + 1}`}
                className="w-full h-full object-cover rounded-xl"
              />
              {noticia.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <ChevronLeft />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <ChevronRight />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {noticia.images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div 
              className="prose prose-invert prose-lg max-w-none whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ 
                __html: noticia.body.replace(/\n/g, '<br />') 
              }}
            />
          </div>
        </article>
      </div>
    </div>
  );
}

export default NoticiaDetail;